import {
    BroadcastResponse,
    Input,
    Output,
    SERVER_URL,
    SESSION_HEARTBEAT_URL,
    SESSION_SPEECH_TOKEN_URL,
    SESSION_URL,
    sessionTranslateUrl
} from '../contexts/Configuration';
import {SessionStartedResponse, SpeechTokenRefreshResponse} from '../reducers/sessionReducer';

/** A piece of (glossary-marked) source text and every target language that shares it. */
export type TranslationGroup = { text: string; to: string[] };
/** One translated string keyed by its target language code. */
export type TranslationItem = { to: string; text: string };

export const SESSION_BALANCE_ERROR_MESSAGE =
    'Your balance is not enough to start this session. Reduce the Maximum Session Time and try again. Otherwise, add funds to your balance. If you believe your balance or pricing is incorrect, contact support.';

export class RosettaClient {

    public static async startSession(input: Input, outputs: Output[], sessionDuration: number, broadcastEnabled: boolean, token: string,
                                     sessionType?: "standard" | "twoWay" | undefined, bidirectionalPanels?: {
            languageName: string;
            languageKey: string;
            color: string;
            inputDevice: { deviceId: string; kind: string; label?: string; groupId?: string } | null;
            outputDevice: {
                deviceId: string;
                kind: string;
                label?: string;
                groupId?: string;
                activePanel?: boolean
            } | null;
            textOnly: boolean
        }[] | undefined) {

        const isTwoWay = sessionType === 'twoWay';
        const body = {
            ...(!isTwoWay && {
                input: input,
                outputs: outputs.map(o => ({
                    ...o,
                    language: {...o.language, voiceName: o.language.voices[o.voiceIndex]?.name}
                })),
            }),
            sessionTimeout: sessionDuration * 60 * 1000, //sent to back in milliseconds
            broadcastEnabled: broadcastEnabled,
            ...(sessionType && {sessionType}),
            ...(bidirectionalPanels && {bidirectionalPanels}),
        };

        return await fetch(`${SERVER_URL}${SESSION_URL}`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
                Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(body)

        })
            .then(response => RosettaClient.marshalSessionResponse(response as SessionStartedResponse))
            .then(response => {
                console.log('SessionStarted response -> ', response);
                return response;
            });
    }

    public static async refreshAzureSpeechToken(authToken: string) {
        const body = {
            type: 'refresh-speech-token'
        }
        return await fetch(`${SERVER_URL}${SESSION_SPEECH_TOKEN_URL}`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
                Authorization: `Bearer ${authToken}`
            },
            body: JSON.stringify(body)

        })
            .then(response => RosettaClient.marshalSpeechTokenRefreshResponse(response as SpeechTokenRefreshResponse))
            .then(response => {
                //console.log('refreshAzureSpeechToken response -> ', response);
                return response;
            });
    }

    public static async sendHeartbeat(token: string, sessionId: string) {
        console.log('-------------> sendHeartbeat [dentro]')
        const body = {sessionId: sessionId};
        return await fetch(`${SERVER_URL}${SESSION_HEARTBEAT_URL}`, {
            method: 'POST',
            headers: {'content-type': 'application/json', Authorization: `Bearer ${token}`},
            body: JSON.stringify(body)
        })
    }

    public static async translate(token: string, sessionId: string, from: string, groups: TranslationGroup[]): Promise<TranslationItem[]> {
        const body = {from, groups};
        const response = await fetch(`${SERVER_URL}${sessionTranslateUrl(sessionId)}`, {
            method: 'POST',
            headers: {'content-type': 'application/json', Authorization: `Bearer ${token}`},
            body: JSON.stringify(body)
        });

        // Read as text first: a non-JSON error body (e.g. a gateway 502/504 HTML page) must not
        // throw an opaque parse error before we can report the real HTTP status.
        const rawBody = await response.text();
        let responseJson: any = {};
        try {
            responseJson = rawBody ? JSON.parse(rawBody) : {};
        } catch {
            // Non-JSON body — leave responseJson empty; the status check below reports it.
        }

        if (!response.ok || !!responseJson.errors) {
            throw new Error(`Translation failed (status ${response.status}): ${responseJson.errors || rawBody || response.statusText}`);
        }
        return (responseJson.translations ?? []) as TranslationItem[];
    }

    private static marshalSessionResponse = async (response: SessionStartedResponse) => {
        const responseJson = await response.json();
        if (!!responseJson.errors) {
            if ('NOT_ENOUGH_CREDITS' === responseJson.errors) {
                throw new Error(SESSION_BALANCE_ERROR_MESSAGE)
            } else if ('AVAILABLE_CREDITS_NOT_ENOUGH_FOR_TIMEOUT' === responseJson.errors) {
                throw new Error(SESSION_BALANCE_ERROR_MESSAGE)
            }

            throw new Error(`Unable to start session: ${responseJson.errors}`)
        }
        return responseJson;
    }

    private static marshalSpeechTokenRefreshResponse = async (response: SpeechTokenRefreshResponse) => {
        if (response.status === 200) {
            const responseJson = await response.json();
            if (!!responseJson.errors) {
                throw new Error(`Unable to start session: ${responseJson.errors}`)
            }
            return responseJson;
        }
        // if (response.status === 400) return response.json().then(r => throw new Error(r.message));
        throw new Error(`Session failed to start (error status ${response.status} - ${response.type}): ${response.statusText}`)
    }

    public static marshalBroadcastResponse = async (response: BroadcastResponse) => {
        if (response.status === 200 || response.status === 400) {
            return await response.json();
        }
        throw new Error(`Unable to retrieve translations (error status ${response.status} - ${response.type}): ${response.statusText}`)
    }
}
